import React, { useCallback } from "react";
import { Tooltip } from "@material-ui/core";
import PlayerAvatar from "./PlayerAvatar";

/**
 * PlayerDetails
 * Displays player information including name, emoji, cash, net worth, and actions remaining
 * Consistent sizing: Base 1rem, Primary 1.1rem, Secondary 0.9rem
 */
const PlayerDetails = ({
  players = [],
  currentTurn,
  name,
  playerCash = {},
  playerNetWorths = {},
  playerColors = {},
  playerEmojis = {},
  actionsRemaining = {},
  maxActionsPerTurn = 3,
  maxActionsThisTurn = {},
  currentPhase = null,
  disconnectedPlayers = [],
  turnTimeRemaining = null,
  turnTimerPlayer = null,
  turnTimerTotal = 90,
  isHost = false,
  socket = null,
  room = null,
  compact = false // Enable compact mode for mobile
}) => {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine timer color based on remaining time
  const getTimerColor = () => {
    if (!turnTimeRemaining || !turnTimerTotal) return '#4CAF50';
    const percentage = (turnTimeRemaining / turnTimerTotal) * 100;
    if (percentage <= 20) return '#f44336'; // Red
    if (percentage <= 40) return '#ff9800'; // Orange
    return '#4CAF50'; // Green
  };

  const getTimerBackgroundColor = () => {
    if (!turnTimeRemaining || !turnTimerTotal) return 'rgba(76, 175, 80, 0.1)';
    const percentage = (turnTimeRemaining / turnTimerTotal) * 100;
    if (percentage <= 20) return 'rgba(244, 67, 54, 0.1)'; // Red
    if (percentage <= 40) return 'rgba(255, 152, 0, 0.1)'; // Orange
    return 'rgba(76, 175, 80, 0.1)'; // Green
  };

  const handleKickPlayer = useCallback((targetPlayer) => {
    if (!socket?.current || !room || !isHost) return;
    socket.current.emit("kick_player", { room, name, targetPlayer });
  }, [socket, room, name, isHost]);

  return (
    <div className="player-details" data-tutorial="player-details">
      {players.map((player, idx) => {
        const maxForPlayer = maxActionsThisTurn[player] ?? maxActionsPerTurn;
        const tooltipText = `${player}${player === name ? ' (You)' : ''}\n💰 Cash: $${playerCash[player] !== undefined ? playerCash[player] : 35}\n💎 Net Worth: $${playerNetWorths[player] !== undefined ? playerNetWorths[player] : 40}${player === currentTurn ? `\n⚡ Actions: ${actionsRemaining[player] || 0}/${maxForPlayer}` : ''}`;
        
        // Only pulse during trading phase when it's the player's turn
        const isActiveTurn = player === currentTurn && currentPhase === "trading";
        const isCurrentTurn = player === currentTurn;
        
        // Build className array to ensure clean class application
        const classNames = ['player-wealth-item'];
        if (isActiveTurn) {
          classNames.push('active');
        }
        if (player === name) {
          classNames.push('current-player');
        }
        if (compact) {
          classNames.push('compact');
        }
        
        const playerColor = playerColors[player] || '#666666';
        
        return (
        <Tooltip title={tooltipText} arrow placement="top" key={`${player}-${currentPhase}-${currentTurn}`}>
          <div
            className={classNames.join(' ')}
            data-active-turn={isActiveTurn ? 'true' : 'false'}
            data-current-phase={currentPhase || 'none'}
            style={{
              borderTop: `2px solid ${playerColor}40`,
              borderRight: `2px solid ${playerColor}40`,
              borderBottom: `2px solid ${playerColor}40`,
              borderLeft: `8px solid ${playerColor}`,
              backgroundColor: `${playerColor}15`,
              boxShadow: isActiveTurn
                ? `0 4px 16px ${playerColor}40`
                : `0 2px 6px ${playerColor}30`,
            }}
        >
          {/* Player order number */}
          <div className={`player-order-number ${isCurrentTurn ? 'active' : ''} ${compact ? 'compact' : ''}`}>
            {idx + 1}
          </div>

          <PlayerAvatar
            playerName={player}
            playerColor={playerColors[player]}
            playerEmoji={playerEmojis[player]}
            size="small"
            showLabel={false}
            className={`player-wealth-avatar${player === name ? ' player-avatar-self' : ''}`}
            selfColor={player === name ? playerColors[player] : undefined}
          />
          
          {/* Player name and stats section */}
          <div className="player-name-section">
            <div className="player-name-row">
            {player === currentTurn && (
                <span className="player-name-turn-indicator">▶</span>
              )}
              <span className="player-name-text">
                {player}
              </span>
              {disconnectedPlayers.includes(player) && (
                <>
                  <span className="player-name-disconnected" title="Disconnected">
                    🔌
                  </span>
                  {isHost && player !== name && (
                    <Tooltip title={`Kick ${player} (replace with bot)`} arrow placement="top">
                      <button
                        className="player-kick-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleKickPlayer(player);
                        }}
                      >
                        ✕
                      </button>
                    </Tooltip>
                  )}
                </>
              )}
              {player === name && (
                <span className="player-name-you-label" style={{ color: playerColors[player] || '#FFD700' }}>
                  (You)
                </span>
              )}
              {/* Turn Timer - Only show for current player whose turn it is */}
              {turnTimeRemaining !== null && turnTimeRemaining !== undefined && 
               player === currentTurn && (
                <div 
                  className="player-timer-container"
                  style={{
                    backgroundColor: getTimerBackgroundColor(),
                    border: `1.5px solid ${getTimerColor()}`,
                  }}
                >
                  <span className="player-timer-icon">⏱️</span>
                  <span 
                    className="player-timer-value"
                    style={{ color: getTimerColor() }}
                  >
                    {formatTime(turnTimeRemaining)}
                  </span>
                </div>
              )}
            </div>
            
            {/* Player Stats Display - Similar to GameActionButtons */}
            <div className={`player-stats-row ${compact ? 'compact' : ''}`}>
              {/* Cash */}
              <div className={`player-stat-item player-stat-item-cash ${compact ? 'compact' : ''}`}>
                <span className={`player-stat-label ${compact ? 'compact' : ''}`}>💰</span>
                <span className={`player-stat-value player-stat-value-cash ${compact ? 'compact' : ''}`}>
                  {formatCurrency(playerCash[player] !== undefined ? playerCash[player] : 35)}
                </span>
              </div>

              {/* Net Worth */}
              <div className={`player-stat-item player-stat-item-networth ${compact ? 'compact' : ''}`}>
                <span className={`player-stat-label ${compact ? 'compact' : ''}`}>💎</span>
                <span className={`player-stat-value player-stat-value-networth ${compact ? 'compact' : ''}`}>
                  {formatCurrency(playerNetWorths[player] !== undefined ? playerNetWorths[player] : 40)}
                </span>
              </div>

              {/* Actions Remaining */}
              <div className={`player-stat-item player-stat-item-actions ${(actionsRemaining[player] || 0) > 0 && player === currentTurn ? 'actions-remaining-active' : ''} ${compact ? 'compact' : ''}`}>
                <span className={`player-stat-label ${compact ? 'compact' : ''}`}>⚡</span>
                <span className={`player-stat-value player-stat-value-actions ${compact ? 'compact' : ''}`}>
                  {actionsRemaining[player] || 0}/{maxActionsThisTurn[player] ?? maxActionsPerTurn}
                </span>
              </div>
            </div>
          </div>
        </div>
        </Tooltip>
        );
      })}
    </div>
  );
};

export default PlayerDetails;

